import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Col, Image, OverlayTrigger, Popover, Row, Tooltip } from "react-bootstrap";
import { Speaker, SpeakerFill, ArrowClockwise, PlusCircle } from "react-bootstrap-icons";
import { CCWRefreshIcon, DownloadIcon, TrashIcon } from "@helloaudio/icons/twotone";
import {
  Checkbox,
  InlineRadioGroup,
  Label,
  LinkButtonBlue,
  Select,
  TextInput,
  Toggle,
  Tooltip as UITooltip,
} from "@helloaudio/ui";
import { useDebounce } from "@helloaudio/hooks";
import { Link } from "react-router-dom";
import { isEmpty, isEqual, truncate } from "lodash";
import { truncateMiddle } from "../../../utils/strings";
import { sendTrackingData } from "../../../utils/tracking";
import { cn } from "../../../utils/styles";

import DeleteEpisodeModal from "./Modal/DeleteEpisodeModal";
import LoadingWrapper from "../../widgets/LoadingWrapper";
import Skeleton from "react-loading-skeleton";
import ReactS3Uploader from "react-s3-uploader";
import moment from "moment";
import ReactAudioPlayer from "react-audio-player";
import ReactTags from "react-tag-autocomplete";
import Datetime from "react-datetime";
import Dropzone from "react-dropzone";
import Pica from "pica";

import "react-loading-skeleton/dist/skeleton.css";
import "react-datetime/css/react-datetime.css";
import "./GetEpisode.scss";

const TITLE_MAX_LENGTH = 100;

const TYPE_LIST = [
  { key: "full", value: "full", label: "Full" },
  { key: "trailer", value: "trailer", label: "Trailer" },
  { key: "bonus", value: "bonus", label: "Bonus" },
];

function GetEpisode({
  downloadTranscript,
  errors,
  existingFeedEpisodeId,
  feed,
  feedEpisode,
  feedId,
  feedTitle,
  getSignedUrl,
  onBlur,
  onChange,
  onSubmit,
  setAudioFileError,
  setBuyCreditsModal,
  setFieldValue,
  totalRemasters,
  values,
}) {
  const formValue = useSelector((state) => state.basic);
  const dispatch = useDispatch();
  const [, setFileInfo] = useState(null);
  const [, setUploadErrors] = useState("");
  const canLimitEpisodesByTags = Boolean(formValue?.localUser?.canLimitEpisodesByTags);
  const tagsPlaceholderText = canLimitEpisodesByTags
    ? "Add new tag"
    : "Upgrade to Pro to use episode tags";

  useEffect(() => {
    if (feedId && isEmpty(feed)) {
      dispatch({ type: "GET_FEED", feedId });
    }
  }, [feedId, feed, dispatch]);

  const truncateFeedFileName = (name) => truncateMiddle(name, { length: 40 });

  const truncateFeedTitle = (title) =>
    truncate(title, { length: TITLE_MAX_LENGTH, separator: " " });

  const fetchedEpisodeStatus = feedEpisode.status ?? "inactive";
  const [episodeStatus, setEpisodeStatus] = useState(fetchedEpisodeStatus);
  useEffect(() => {
    setEpisodeStatus(fetchedEpisodeStatus);
  }, [fetchedEpisodeStatus]);

  const onTextBlurWrapper = (e) => {
    onBlur(e);
    const fieldName = e.target.name;
    if (fieldName === "title" && values.title.trim() && values.title.trim() !== feedEpisode.title) {
      return onSubmit(values);
    }
    if (
      fieldName === "season" &&
      formValue.feed.use_seasons &&
      values.season >= 1 &&
      values.season !== feedEpisode.season
    ) {
      return onSubmit(values);
    }

    if (
      fieldName === "dripAfter" &&
      values.dripAfter >= 0 &&
      values.dripAfter !== feedEpisode.drip_after
    ) {
      return onSubmit(values);
    }

    if (
      fieldName === "expirationDripAfter" &&
      values.expirationDripAfter >= 0 &&
      values.expirationDripAfter !== feedEpisode.expiration_drip_after
    ) {
      return onSubmit(values);
    }
  };

  const onCustomBlurWrapper = (e) => {
    const fieldName = e.name;
    if (fieldName === "tags" && !isEqual(values.tags, feedEpisode.tags)) {
      return onSubmit(values);
    }

    if (fieldName === "episodeType" && e.value !== feedEpisode.episode_type) {
      return onSubmit({ ...values, episodeType: e.value });
    }

    if (fieldName === "expiry") {
      const currentExpiryValue = feedEpisode.expiration_drip_after ? "after" : "never";
      if (e.value !== currentExpiryValue) {
        if ((e.value === "after" && values.expirationDripAfter) || e.value === "never") {
          return onSubmit({ ...values, expiry: e.value });
        }
      }
    }

    if (fieldName === "publish_date") {
      const publishDate = moment(values.publish_date).unix();
      const currentPublishDate = moment(feedEpisode.publish_date).unix();
      if (
        formValue.feedEpisode.feed_type === "date" &&
        !isNaN(publishDate) &&
        publishDate !== currentPublishDate
      ) {
        return onSubmit(values);
      }
    }
  };

  const onBlurWrapper = (e) => {
    if (e && e.target && e.target.name) {
      return onTextBlurWrapper(e);
    }

    if (e.name) {
      return onCustomBlurWrapper(e);
    }
  };

  const renderFileName = () => {
    const fileName = String(feedEpisode?.original_filename).trim();
    const truncatedFileName = truncateFeedFileName(fileName);
    const fileNameFragment = <span className="tw-font-normal tw-pl-2">{truncatedFileName}</span>;
    if (truncatedFileName.length < fileName.length) {
      return (
        <UITooltip bgColor="#2E20B1" label={fileName} placement="top">
          {fileNameFragment}
        </UITooltip>
      );
    }
    return fileNameFragment;
  };

  const onDeleteTag = (i) => {
    if (canLimitEpisodesByTags) {
      const tagsCopy = [...values.tags];
      tagsCopy.splice(i, 1);
      setFieldValue("tags", tagsCopy);
    }
  };

  const onBlurTag = useDebounce(() => {
    onBlurWrapper({ name: "tags" });
  }, 500);

  const handleDrop = async (acceptedFiles) => {
    dispatch({ type: "SET_LOADING", key: "upload-image", loading: true });
    setUploadErrors("");
    const pica = Pica();

    const processFile = async (file) => {
      setFileInfo(file);
      try {
        // Read file as Data URL
        const fileDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Load image
        const img = new window.Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error("Image load failed. Please try a different image."));
          img.src =
            typeof fileDataUrl === "string"
              ? fileDataUrl
              : URL.createObjectURL(new Blob([fileDataUrl]));
        });

        // Calculate crop
        const srcAspect = img.width / img.height;
        const destSize = 1500;
        const destAspect = 1;
        let sx = 0,
          sy = 0,
          sw = img.width,
          sh = img.height;
        if (srcAspect > destAspect) {
          sw = img.height * destAspect;
          sx = (img.width - sw) / 2;
        } else if (srcAspect < destAspect) {
          sh = img.width / destAspect;
          sy = (img.height - sh) / 2;
        }

        // Draw cropped image to source canvas
        const srcCanvas = document.createElement("canvas");
        srcCanvas.width = sw;
        srcCanvas.height = sh;
        const ctx = srcCanvas.getContext("2d");
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

        // Resize with pica
        const destCanvas = document.createElement("canvas");
        destCanvas.width = destSize;
        destCanvas.height = destSize;
        await pica.resize(srcCanvas, destCanvas, {
          unsharpAmount: 80,
          unsharpRadius: 0.6,
          unsharpThreshold: 2,
        });
        const resized = await pica.toBlob(destCanvas, "image/jpeg", 0.9);

        // Convert blob to base64 and dispatch upload
        const base64 = await new Promise((resolve, reject) => {
          const blobReader = new FileReader();
          blobReader.onloadend = () => resolve(blobReader.result);
          blobReader.onerror = reject;
          blobReader.readAsDataURL(resized);
        });

        dispatch({
          type: "UPLOAD_FEED_EPISODE_IMAGE",
          photoBinaryString: base64.split(",")[1],
          contentType: "image/jpeg",
          feedEpisodeId: existingFeedEpisodeId,
          feedId,
        });
      } catch (err) {
        setUploadErrors(err.message || "Image processing failed. Please try a different image.");
      }
    };

    for (const file of acceptedFiles) {
      await processFile(file);
    }
  };

  const handleDropRejected = (fileRejections) => {
    for (const file of fileRejections) {
      for (const err of file.errors) {
        if (err.code === "file-too-large") {
          setUploadErrors(
            "Error: File selected is 20mb or more. Recommended image size is 3000x3000.",
          );
          return;
        }
        if (err.code === "file-invalid-type") {
          setUploadErrors(`Error: ${err.message}`);
          return;
        }
      }
    }

    setUploadErrors("Error: File could not be uploaded. Please check the file type and size.");
  };

  const handleDeleteFeedEpisodeImage = () => {
    dispatch({
      type: "DELETE_FEED_EPISODE_IMAGE",
      feedEpisodeId: existingFeedEpisodeId,
      feedId,
    });
  };

  return (
    <React.Fragment>
      <div className="tw-flex tw-gap-4 tw-mb-4">
        <div>
          <LoadingWrapper
            loadingState={
              formValue.loading["upload-episode-image"] || formValue.loading["delete-episode-image"]
            }
            className="h-100"
          >
            <Dropzone
              multiple={false}
              onDropAccepted={(e) => handleDrop(e)}
              onDropRejected={(e) => handleDropRejected(e)}
              accept={{
                "image/jpeg": [".jpg", ".jpeg"],
                "image/png": [],
                "image/gif": [],
              }}
              maxSize={20971520}
            >
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className="tw-cursor-pointer tw-group tw-relative">
                  <input {...getInputProps()} />
                  <Image
                    className="tw-hidden md:tw-block feed-image"
                    src={`https://d32kcwy5dai345.cloudfront.net/${
                      feedEpisode.image || feed.s3_key
                    }`}
                  />
                  {feedEpisode.image ? (
                    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300">
                      <p className="tw-text-white tw-bg-slate-500 tw-text-[10px] tw-p-2 tw-rounded-md">
                        Update Image
                      </p>
                    </div>
                  ) : (
                    <div className="tw-text-[10px] tw-text-indigo-800 tw-mt-2 tw-text-center">
                      Add Episode Image
                    </div>
                  )}
                </div>
              )}
            </Dropzone>
            {feedEpisode.image && (
              <div
                className="tw-flex tw-items-center tw-mt-2 tw-cursor-pointer"
                onClick={handleDeleteFeedEpisodeImage}
              >
                <TrashIcon className="tw-w-5 tw-h-5 tw-text-red-500 tw-cursor-pointer" />
                <span className="tw-text-[10px] tw-font-bold tw-text-red-500 tw-ml-1">
                  Delete Image
                </span>
              </div>
            )}
          </LoadingWrapper>
        </div>
        <div className="tw-flex tw-flex-col">
          {feedTitle ? (
            <div className="title-semibold tw-mb-2 tw-w-[100%]">
              <OverlayTrigger
                delay={{ show: feedTitle.length < TITLE_MAX_LENGTH ? 60000 : 250, hide: 400 }}
                placement="auto"
                overlay={
                  <Popover id="my" className="audio-inbox-tooltip">
                    <div className="title-medium">{feedTitle}</div>
                  </Popover>
                }
              >
                <div className={cn("title-semibold tw-inline-block tw-w-[100%]")}>
                  Feed for {truncateFeedTitle(feedTitle)}
                </div>
              </OverlayTrigger>
              <div className="tw-text-sm tw-font-normal tw-mt-4">
                <Link to={`/feed/${feedId}`} className="tw-text-indigo-800">
                  &#8592; Return to feed
                </Link>
              </div>
            </div>
          ) : (
            <div className="tw-mb-4">
              <Skeleton
                height="40%"
                className="title-semibold ellipsis-feed-title tw-cursor-pointer tw-mb-2"
              />
              <Skeleton
                height="40%"
                className="title-semibold ellipsis-feed-title tw-cursor-pointer"
              />
            </div>
          )}
        </div>
      </div>

      <TextInput
        className="tw-mb-4 tw-w-full"
        helpText={
          <p>
            Possible variables: <br />
            [[fname|<em>fallback</em>]]
            <br />
            [[lname|<em>fallback</em>]]
          </p>
        }
        label="Episode Title"
        name="title"
        onBlur={onBlurWrapper}
        onChange={onChange}
        preventSubmitOnEnter
        type="text"
        value={values?.title || ""}
      />
      <div className="tw-flex tw-justify-between tw-items-center tw-py-1 tw-font-Montserrat tw-mb-4 title-semibold">
        <div className="tw-flex tw-items-center">
          Status: &nbsp;
          <LoadingWrapper loadingKey={`save-episode-published`} smallBlock>
            <Toggle
              id={`published-${feedEpisode.feed_episode_id}`}
              value={episodeStatus === "active"}
              checked={episodeStatus === "active"}
              className="tw-mr-2 tw-font-Montserrat has-gutters"
              onChange={(e) => {
                const status = e ? "active" : "inactive";
                setEpisodeStatus(status);
                dispatch({
                  type: "UPDATE_EPISODE_STATUS",
                  feedEpisodeId: existingFeedEpisodeId,
                  status,
                });
              }}
            />
          </LoadingWrapper>
          <div className="tw-font-Montserrat tw-text-center tw-rounded">
            {episodeStatus === "pending" ? (
              <span>Still processing...</span>
            ) : episodeStatus === "active" ? (
              <React.Fragment>
                {feedEpisode.feed_type === "drip" ||
                feedEpisode.feed_type === "instant" ||
                (feedEpisode.feed_type === "date") &
                  (moment() > moment(feedEpisode.publish_date)) ? (
                  <div
                    className="tw-p-2 tw-rounded title-medium "
                    style={{ background: "#f0fcf3", color: "#00CF2E" }}
                  >
                    <span>Published</span>
                  </div>
                ) : (
                  <div
                    className="tw-p-2 tw-rounded title-medium "
                    style={{ background: "#fffaf0", color: "#F3A100" }}
                  >
                    <span>Scheduled</span>
                  </div>
                )}
              </React.Fragment>
            ) : (
              <div
                className="tw-p-2 tw-rounded title-medium "
                style={{ background: "#fafafa", color: "#9F9F9F" }}
              >
                <span>Unpublished</span>
              </div>
            )}
          </div>
        </div>
        <DeleteEpisodeModal
          existingFeedEpisodeId={existingFeedEpisodeId}
          feedEpisode={feedEpisode}
          values={values}
        />
      </div>
      {feedEpisode?.original_filename ? (
        <div className="tw-mb-4 tw-w-full tw-text-sm tw-font-Montserrat tw-font-semibold">
          File Name: {renderFileName()}
        </div>
      ) : null}
      <div className="episode-uploader mb-2">
        <ReactS3Uploader
          className="tw-cursor-pointer btn btn-link file"
          getSignedUrl={getSignedUrl}
          onProgress={(progress) => dispatch({ type: "UPDATE_SINGLE_EPISODE_PROGRESS", progress })}
          // onError={(error) => props.setError(error)}
          onFinish={() => {
            dispatch({
              type: "FINISH_SINGLE_EPISODE_UPLOAD",
              feedEpisodeId: existingFeedEpisodeId,
            });
            sendTrackingData("upload_episode", {
              feedId,
              feedEpisodeId: existingFeedEpisodeId,
            });
          }}
          uploadRequestHeaders={{}}
          contentDisposition="auto"
          accept="audio/*, video/*, audio/mpeg, audio/mp3, audio/m4a, audio/mp4, audio/mp4a-latm, audio/x-m4a"
        />
        <LinkButtonBlue className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer episode-uploader-text ml-3">
          <CCWRefreshIcon
            className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary"
            style={{ marginRight: "10px" }}
          />
          <div className="tw-flex tw-text-sm text-btn-primary">Replace Media</div>
        </LinkButtonBlue>
      </div>
      <div className="tw-mb-3 tw-w-full tw-text-sm tw-font-Montserrat tw-font-semibold">
        Player Preview
      </div>
      <LoadingWrapper
        loadingKey="auth0-user"
        smallBlock
        className="tw-mb-4 d-flex align-items-center"
      >
        {feedEpisode?.dolby_mastered_s3_key &&
        values?.remasterStatus === "complete" &&
        values?.useDolby === true ? (
          <React.Fragment>
            <ReactAudioPlayer
              key={feedEpisode?.dolby_mastered_s3_key}
              className="react-audio-player mr-3"
              src={feedEpisode?.dolby_mastered_s3_key}
              controls
              onError={() =>
                setAudioFileError({
                  audioFileError: true,
                })
              }
              title=" "
            />
            <OverlayTrigger
              placement="right-start"
              overlay={<Tooltip>Using remastered audio, powered by Dolby.io</Tooltip>}
            >
              <span
                className="d-flex align-items-center"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  setFieldValue("useDolby", !!e.target.checked);
                  dispatch({ type: "SET_SUCCESS", success: "Original file quality now in use." });
                }}
              >
                <SpeakerFill color="#2E20B1" size={25} />
              </span>
            </OverlayTrigger>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <ReactAudioPlayer
              key={feedEpisode.s3_key}
              className="react-audio-player mr-3"
              src={feedEpisode.s3_key}
              controls
              onError={() =>
                setAudioFileError({
                  audioFileError: true,
                })
              }
              title=" "
            />
            {values.remasterStatus === "complete" ? (
              <OverlayTrigger
                placement="right-start"
                overlay={<Tooltip>Publish with remastered audio, powered by Dolby.io.</Tooltip>}
              >
                <span
                  className="d-flex align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    setFieldValue("useDolby", !e.target.checked);
                    dispatch({
                      type: "SET_SUCCESS",
                      success: "Using remastered audio, powered by Dolby.io.",
                    });
                  }}
                >
                  <Speaker color="#777" size={25} />
                </span>
              </OverlayTrigger>
            ) : values.remasterStatus ? (
              <p className="my-auto">Remaster Still in Progress</p>
            ) : (
              <LoadingWrapper loadingKey="remaster-in-progress">
                <OverlayTrigger
                  placement="right-start"
                  overlay={
                    <Tooltip>
                      Publish with remastered audio, powered by Dolby.io.{" "}
                      {totalRemasters === 1
                        ? `${totalRemasters} remaster`
                        : `${totalRemasters} remasters`}{" "}
                      available this month.
                    </Tooltip>
                  }
                >
                  <span
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={
                      totalRemasters > 0
                        ? () =>
                            dispatch({
                              type: "REMASTER_AUDIO_FILE",
                              audioFileId: feedEpisode.audio_file_id,
                              callback: () => {
                                values.remasterStatus = "Remaster Processing";
                                setFieldValue("useDolby", true);
                                dispatch({
                                  type: "GET_FEED_EPISODE",
                                  feedEpisodeId: feedEpisode.feed_episode_id,
                                  silent: true,
                                });
                              },
                            })
                        : () => setBuyCreditsModal(true)
                    }
                  >
                    <Speaker color="#777" size={25} />
                  </span>
                </OverlayTrigger>
              </LoadingWrapper>
            )}
          </React.Fragment>
        )}
      </LoadingWrapper>
      <LoadingWrapper loadingKey="transcribe-episode tw-mb-4">
        {values.transcript && values.transcript !== "Transcript Still Processing" && (
          <LinkButtonBlue
            onClick={downloadTranscript}
            className="tw-p-0 text-btn-primary tw-flex tw-items-center tw-cursor-pointer episode-uploader-text"
          >
            <DownloadIcon className="tw-w-4 tw-mr-2 text-btn-primary" />
            <div className="tw-flex tw-text-sm text-btn-primary">Download Transcript</div>
          </LinkButtonBlue>
        )}
        {values.transcript && values.transcript === "Transcript Still Processing" && (
          <div className="tw-flex tw-flex-row tw-items-center">
            <LinkButtonBlue
              onClick={() =>
                dispatch({
                  type: "TRANSCRIBE_EPISODE",
                  feedEpisodeId: existingFeedEpisodeId,
                  callback: () => {
                    values.transcript = "Transcript Still Processing";
                  },
                })
              }
              className="tw-p-0 text-btn-primary tw-flex tw-items-center tw-cursor-pointer episode-uploader-text"
            >
              <OverlayTrigger placement="auto" overlay={<Tooltip>Regenerate Transcript</Tooltip>}>
                <ArrowClockwise size={20} className="tw-mr-2 text-btn-primary" />
              </OverlayTrigger>
            </LinkButtonBlue>
            <div className="tw-flex tw-text-sm tw-font-Montserrat tw-font-semibold">
              Transcript in progress
            </div>
          </div>
        )}
        {!values.transcript && values.transcript !== "Transcript Still Processing" && (
          <LinkButtonBlue
            onClick={() =>
              dispatch({
                type: "TRANSCRIBE_EPISODE",
                feedEpisodeId: existingFeedEpisodeId,
                callback: () => {
                  values.transcript = "Transcript Still Processing";
                },
              })
            }
            className="tw-p-0 text-btn-primary tw-flex tw-items-center tw-cursor-pointer episode-uploader-text"
          >
            <PlusCircle size={20} className="tw-mr-2 text-btn-primary" />
            <div className="tw-flex tw-text-sm text-btn-primary">Generate Transcript</div>
          </LinkButtonBlue>
        )}
      </LoadingWrapper>
      {!feedEpisode.is_public ? (
        <>
          <Label className="tw-mt-6 tw-mb-2 tw-text-sm">
            Limit episode to those with any of the following tag(s)
          </Label>
          <ReactTags
            allowNew={canLimitEpisodesByTags}
            autoresize
            classNames={{
              root: cn("tags-class tw-font-Montserrat tw-text-sm tw-z-10", {
                "tw-cursor-not-allowed tw-bg-gray-100": !canLimitEpisodesByTags,
              }),
              rootFocused: "tags-focused-class",
              searchInput: cn("react-tags__search-input", {
                "tw-cursor-not-allowed tw-bg-gray-100": !canLimitEpisodesByTags,
              }),
              selected: "react-tags__selected",
              selectedTag: "selected-tag-class",
            }}
            delimiters={["Enter", "Tab", ","]}
            onAddition={(tag) => {
              if (canLimitEpisodesByTags) {
                setFieldValue("tags", [...values.tags, { id: tag.name, name: tag.name }]);
              }
            }}
            inputAttributes={{ disabled: !canLimitEpisodesByTags }}
            onBlur={onBlurTag}
            onDelete={onDeleteTag}
            placeholderText={tagsPlaceholderText}
            suggestions={(formValue.tagSuggestions || []).map((tag) => ({
              ...tag,
              disabled: !canLimitEpisodesByTags,
            }))}
            tags={values.tags}
          />
          {!canLimitEpisodesByTags ? (
            <div className="tw-mt-2 tw-text-sm tw-text-gray-700">
              Episode tagging is a Pro plan feature. Upgrade to Pro to add or edit episode tags.
            </div>
          ) : null}
        </>
      ) : null}
      <Row>
        <Col md={4}>
          <Select
            className="tw-my-4"
            label="Episode Type"
            labelClassName="tw-text-sm"
            name="episodeType"
            onChange={(value) => {
              setFieldValue("episodeType", value);
              onBlurWrapper({ name: "episodeType", value });
            }}
            options={
              TYPE_LIST?.map((list) => ({
                label: list.label,
                value: list.value,
                key: list.key,
              })) || []
            }
            placeholder="Choose the type"
            value={values?.episodeType || "full"}
          />
        </Col>
      </Row>
      {feed.use_seasons && (
        <TextInput
          className="tw-mb-4 tw-w-full"
          label="Season"
          name="season"
          onBlur={onBlurWrapper}
          onChange={onChange}
          preventSubmitOnEnter
          type="number"
          value={values?.season || ""}
        />
      )}
      {feedEpisode.feed_type === "drip" && (
        <React.Fragment>
          <Label className="tw-cursor-pointer tw-mb-2 tw-text-sm">
            Drips this many days after previous episode:
          </Label>
          <Row>
            <Col md={10}>
              <TextInput
                className="tw-mb-4 tw-w-[120px]"
                error={errors.dripAfter}
                name="dripAfter"
                onBlur={onBlurWrapper}
                onChange={onChange}
                preventSubmitOnEnter
                type="number"
                value={values?.dripAfter ?? ""}
              />
            </Col>
          </Row>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <InlineRadioGroup
              disabled={!formValue.localUser.canAdvancedFeedCustomization}
              grow
              id="drip_expiration"
              label="Drip Expiration:"
              name="expiry"
              onChange={(value) => {
                setFieldValue("expiry", value);
                onBlurWrapper({ name: "expiry", value });
              }}
              options={[
                {
                  key: "never",
                  value: "never",
                  label: "Never",
                },
                {
                  key: "after",
                  value: "after",
                  label:
                    "This many days after initially dripping" +
                    (formValue.localUser.canAdvancedFeedCustomization
                      ? ""
                      : " (Upgrade to Powerhouse)"),
                },
              ]}
              value={values?.expiry || "never"}
            />
            {values?.expiry === "after" && (
              <TextInput
                className="tw-w-[120px]"
                error={errors.expirationDripAfter}
                label="Days"
                name="expirationDripAfter"
                onBlur={onBlurWrapper}
                onChange={onChange}
                preventSubmitOnEnter
                type="number"
                value={values?.expirationDripAfter || ""}
              />
            )}
          </div>
        </React.Fragment>
      )}
      {feedEpisode.feed_type === "date" && (
        <Row className="get-episode-publish-date-container">
          <Col md={6}>
            <Datetime
              className="publish-date-picker"
              name="publish_date"
              onChange={(value) => setFieldValue("publish_date", value)}
              onClose={() => onBlurWrapper({ name: "publish_date" })}
              value={moment(values.publish_date)}
              dateFormat="D MMM YYYY"
              inputProps={{
                autoComplete: "off",
                placeholder: "Enter Publish Date",
                className: "form-control tw-text-sm tw-font-Montserrat",
              }}
            />
            {errors.publishDate && <span className="manual-error">{errors.publishDate}</span>}
          </Col>
        </Row>
      )}
      {feedEpisode.is_public && (
        <Checkbox
          className="tw-my-4"
          label="Explicit Content"
          labelClassName="tw-text-sm"
          name="explicit"
          onChange={(event) => setFieldValue("explicit", !!event.target.checked)}
          onBlur={onBlurWrapper}
          checked={values.explicit}
        />
      )}
    </React.Fragment>
  );
}

GetEpisode.propTypes = {
  downloadTranscript: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  existingFeedEpisodeId: PropTypes.string.isRequired,
  feed: PropTypes.object.isRequired,
  feedEpisode: PropTypes.object.isRequired,
  feedId: PropTypes.string,
  feedTitle: PropTypes.string,
  getSignedUrl: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setAudioFileError: PropTypes.func.isRequired,
  setBuyCreditsModal: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  src: PropTypes.string,
  totalRemasters: PropTypes.number.isRequired,
  values: PropTypes.object.isRequired,
};

export default GetEpisode;
